import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hoursToMs } from '@wwe-portal/utils/date';
import { MAX_REFETCH_INTERVAL } from '../../utils/config';
import { useIsAuthenticated } from '../stores/auth-store';

import {
  claimSlotType,
  getClaimHistory,
  getLastestStampCard,
  getStampCard,
} from '../../services/client/stampcard';
import { getCurrentPrizeStartDate } from '@wwe-portal/web/components/stampcard/Provider/mapper';
import { useInvalidateCurrentUser } from './user-info';
import { StampCardSlotModel } from '../../components/stampcard/Provider/type';

const QUERY_KEY_PREFIX = 'stampcard';

export const useInvalidateStampCard = () => {
  const rbeV2Enabled = true;
  const queryClient = useQueryClient();
  return async () => {
    if (rbeV2Enabled) {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY_PREFIX] });
    }
  };
};

interface GetStampCardParams {
  stampCardId?: number;
  standardSlots?: StampCardSlotModel[];
}

const DEFAULT_STAMPCARD_INTERVAL_MS = 60_000;
const JOB_LATENCY_MS = 35_000;
export const useGetStampcard = (params: GetStampCardParams) => {
  const isAuthenticated = useIsAuthenticated();
  const QUERY_KEYS = [QUERY_KEY_PREFIX, 'get', params];
  const queryClient = useQueryClient();
  const forceUpdateOrder = () => {
    queryClient.setQueryData(QUERY_KEYS, (data) => data);
  };
  const queryResult = useQuery({
    queryKey: QUERY_KEYS,
    queryFn: () => getStampCard({ stampCardId: params.stampCardId }),
    enabled: isAuthenticated && params.stampCardId != null,
    refetchInterval: (data) => {
      // if not stampcards active, use default stampcard interval
      if (
        data == null ||
        !params.standardSlots ||
        params.standardSlots.length === 0
      ) {
        return DEFAULT_STAMPCARD_INTERVAL_MS;
      }

      const minSlotTimer = Math.min(
        ...params.standardSlots.map((slot) => slot.slotTimer)
      );
      // if slotTimer is invalid, use default stampcard interval
      if (minSlotTimer === 0) return DEFAULT_STAMPCARD_INTERVAL_MS;

      const now = Date.now();
      const currentStartDate = getCurrentPrizeStartDate({
        startDate: params?.standardSlots?.[0]?.startDate,
        endDate: params?.standardSlots?.[0]?.endDate,
        slotTimer: minSlotTimer,
        now,
      });

      // get remaining time in miliseconds til daily stampcard reset time + job latency time
      const getRenewDailyInMiliseconds = () => {
        if (currentStartDate == null) return DEFAULT_STAMPCARD_INTERVAL_MS;
        const msCurrentEndDate =
          currentStartDate.getTime() + hoursToMs(minSlotTimer);
        return Math.min(
          MAX_REFETCH_INTERVAL,
          msCurrentEndDate - now + JOB_LATENCY_MS
        );
      };

      // if user hasn't claimed any items, refetch at daily stampcard reset time
      // if (data.data.data.standard.length === 0) {
      //   return getRenewDailyInMiliseconds();
      // }

      // get last time job run
      // const lastedUpdatedAt = Math.max(
      //   ...(data.data.data.standard.map((slot) => toEpoch(slot.updated_at)) ??
      //     [])
      // );

      // if daily stampcard is resetted and job has not run yet, use FAST_STAMPCARD_INTERVARL_MS
      // if (currentStartDate && currentStartDate.getTime() > lastedUpdatedAt) {
      //   return FAST_STAMPCARD_INTERVARL_MS;
      // }

      // Refetch at daily stampcard reset time
      return getRenewDailyInMiliseconds();
    },
    refetchOnWindowFocus: false,
  });
  return {
    forceUpdateOrder,
    ...queryResult,
  };
};

export const useGetClaimHistory = (params: GetStampCardParams) => {
  const isAuthenticated = useIsAuthenticated();
  const QUERY_KEYS = [QUERY_KEY_PREFIX, 'get_claim', params];
  const queryClient = useQueryClient();
  const forceUpdateOrder = () => {
    queryClient.setQueryData(QUERY_KEYS, (data) => data);
  };
  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEYS,
    queryFn: () => getClaimHistory(params),
    enabled: isAuthenticated && params?.stampCardId != null,
    refetchOnWindowFocus: false,
  });
  return {
    forceUpdateOrder,
    claimedHistory: data?.data.data,
    isLoading,
  };
};

export const useGetLastestStampCard = () => {
  const QUERY_KEYS = [QUERY_KEY_PREFIX, 'get_lastest'];
  const queryClient = useQueryClient();
  const forceUpdateOrder = () => {
    queryClient.setQueryData(QUERY_KEYS, (data) => data);
  };
  const queryResult = useQuery({
    queryKey: QUERY_KEYS,
    queryFn: async () => {
      const response = await getLastestStampCard();
      const { standard, premium } = response.data.data;
      return { standard, premium };
    },
    refetchOnWindowFocus: false,
    refetchInterval: (data: any) => {
      if (data == null) return false;
      const standard = data?.standard || [];
      const premium = data?.premium || [];
      const slots = [...premium, ...standard];
      const minEndDate = Math.min(
        ...slots.map((slot: any) => Date.parse(slot.endDate))
      );
      const now = Date.now();
      if (minEndDate > now) {
        return Math.min(MAX_REFETCH_INTERVAL, minEndDate - now + 1000);
      }
      return false;
    },
  });
  return {
    forceUpdateOrder,
    ...queryResult,
  };
};

interface ClaimSlotTypeParams {
  stampCardId: number;
  slotTypeId: number;
  slotId: string;
  slotItemId: string;
}
export const usePutClaimSlotType = () => {
  const invalidateStampcard = useInvalidateStampCard();
  const invalidateUserInfo = useInvalidateCurrentUser();
  const QUERY_KEYS = [QUERY_KEY_PREFIX, 'claim_slot'];
  return useMutation({
    mutationKey: QUERY_KEYS,
    mutationFn: (params: ClaimSlotTypeParams) => claimSlotType(params),
    onSettled: async () => {
      await invalidateStampcard();
      await invalidateUserInfo();
    },
  });
};
